import { createContext, useContext, useEffect, useState, useMemo } from 'react';
import { useLocation, useRouteMatch } from 'quartz-routing/react-router';
import { DateFilterType, DEFAULT_FILTER_VALUES, DEFAULT_SORT_VALUE, TicketListingView, sortFieldsToStrings } from '../constants/filters';
import { UserTicketStatusOption, UserTicketViewOption } from '../data/types';
import { ROUTES } from '../constants/routes';
import { jsx as _jsx } from "react/jsx-runtime";
function stringsToSortFields(strings) {
  return strings.map(s => {
    const isDesc = s.startsWith('-');
    return {
      propertyName: isDesc ? s.slice(1) : s,
      direction: isDesc ? 'DESCENDING' : 'ASCENDING'
    };
  });
}
const DEFAULT_STATE = {
  view: TicketListingView.TABLE,
  sort: DEFAULT_SORT_VALUE,
  offset: 0,
  createDateFilter: DEFAULT_FILTER_VALUES.createDateFilter,
  lastActivityFilter: DEFAULT_FILTER_VALUES.lastActivityFilter,
  statusFilter: DEFAULT_FILTER_VALUES.statusFilter,
  ticketView: DEFAULT_FILTER_VALUES.ticketView,
  searchString: '',
  conversationSearch: ''
};
function getDefaultState(portalData) {
  const hasCreatedateColumn = portalData.configuredTicketListProperties.some(({
    propertyName
  }) => propertyName === 'createdate');
  return Object.assign({}, DEFAULT_STATE, {
    createDateFilter: hasCreatedateColumn ? DEFAULT_FILTER_VALUES.createDateFilter : DEFAULT_FILTER_VALUES.createDateFilterWithoutColumn
  });
}
function readEnumParam(params, key, enumObject, fallback) {
  var _Object$values$find;
  const value = params.get(key);
  return (_Object$values$find = Object.values(enumObject).find(option => option === value)) !== null && _Object$values$find !== void 0 ? _Object$values$find : fallback;
}
function readFromUrl(search, defaults) {
  var _params$get, _params$get2, _params$get3;
  const params = new URLSearchParams(search);
  const sortRaw = params.get('sort');
  let sort = defaults.sort;
  try {
    if (sortRaw) sort = stringsToSortFields(JSON.parse(sortRaw));
  } catch (_unused) {
    // ignore malformed sort param
  }
  return {
    view: readEnumParam(params, 'view', TicketListingView, defaults.view),
    sort,
    offset: Number((_params$get = params.get('offset')) !== null && _params$get !== void 0 ? _params$get : 0),
    createDateFilter: readEnumParam(params, 'createdate', DateFilterType, defaults.createDateFilter),
    lastActivityFilter: readEnumParam(params, 'hs_lastactivitydate', DateFilterType, defaults.lastActivityFilter),
    statusFilter: readEnumParam(params, 'statusFilter', UserTicketStatusOption, defaults.statusFilter),
    ticketView: readEnumParam(params, 'ticketView', UserTicketViewOption, defaults.ticketView),
    searchString: (_params$get2 = params.get('searchString')) !== null && _params$get2 !== void 0 ? _params$get2 : '',
    conversationSearch: (_params$get3 = params.get('conversationSearch')) !== null && _params$get3 !== void 0 ? _params$get3 : ''
  };
}
function setOrDelete(params, key, value, defaultValue) {
  if (value === defaultValue || !value && !defaultValue) {
    params.delete(key);
  } else {
    params.set(key, value);
  }
}
function writeStateToParams(params, state, defaults) {
  setOrDelete(params, 'view', state.view, defaults.view);
  setOrDelete(params, 'sort', JSON.stringify(sortFieldsToStrings(state.sort)), JSON.stringify(sortFieldsToStrings(defaults.sort)));
  setOrDelete(params, 'offset', `${state.offset}`, '0');
  setOrDelete(params, 'createdate', state.createDateFilter, defaults.createDateFilter);
  setOrDelete(params, 'hs_lastactivitydate', state.lastActivityFilter, defaults.lastActivityFilter);
  setOrDelete(params, 'statusFilter', state.statusFilter, defaults.statusFilter);
  setOrDelete(params, 'ticketView', state.ticketView, defaults.ticketView);
  setOrDelete(params, 'searchString', state.searchString, '');
  setOrDelete(params, 'conversationSearch', state.conversationSearch, '');
}
const ListingsSortAndFiltersContext = /*#__PURE__*/createContext(null);
export function ListingsSortAndFiltersProvider({
  children,
  portalData
}) {
  const location = useLocation();
  const isOnListingsRoute = !!useRouteMatch({
    path: ROUTES.root,
    exact: true
  });
  const isOnTicketRoute = !!useRouteMatch({
    path: ROUTES.ticket
  });
  const defaultState = useMemo(() => getDefaultState(portalData), [portalData]);
  const [filters, setFiltersState] = useState(() => readFromUrl(location.search, defaultState));
  useEffect(() => {
    if (!isOnListingsRoute && !isOnTicketRoute) return;
    const params = new URLSearchParams();
    writeStateToParams(params, filters, defaultState);
    const search = params.toString();
    const url = search ? `?${search}` : window.location.pathname;
    //TODO: Consider swapping for history.replace
    window.history.replaceState(null, '', url);
  }, [location.pathname, isOnListingsRoute, isOnTicketRoute, filters, defaultState]);
  const contextValue = useMemo(() => Object.assign({}, filters, {
    defaults: defaultState,
    setFiltersState
  }), [filters, defaultState, setFiltersState]);
  return /*#__PURE__*/_jsx(ListingsSortAndFiltersContext.Provider, {
    value: contextValue,
    children: children
  });
}
export function useListingsSortAndFiltersContext() {
  const context = useContext(ListingsSortAndFiltersContext);
  if (!context) {
    throw new Error('useListingsSortAndFiltersContext must be used within a ListingsSortAndFiltersProvider');
  }
  return context;
}